import { EventType } from "@/analytics/analytics-events";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import { BackgroundTask } from "@/utils/background-tasks";
import { FaroText, FontWeights, InfoIcon, grey } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
} from "@faro-lotv/service-wires";
import { Box, Divider, Popover, Stack } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
} from "react";
import { CloudActivityMenuButton } from "./progress-overview-button";
import { ProgressOverviewFeed } from "./progress-overview-feed";
import {
  ProcessInfoSource,
  selectProcesses,
} from "./progress-overview-selectors";
import {
  ProgressMenuLayer,
  calculateProgressMenuZIndex,
} from "./progress-overview-z-index";

/** @returns a menu to track the upload and processing of PointClouds */
export function ProgressOverviewMenu(): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const [hasNewTasks, setHasNewTasks] = useState(false);

  const processes = useAppSelector(selectProcesses, isEqual);
  const handledTasks = useRef(processes);

  useEffect(() => {
    if (!isOpen) {
      const newTasks: ProcessInfoSource[] = [];
      for (const process of processes) {
        const handledTask = handledTasks.current.find(
          (task) => process.id === task.id,
        );

        if (
          (!handledTask && process.state !== BackgroundTaskState.succeeded) ||
          (!!handledTask && handledTask.state !== process.state)
        ) {
          newTasks.push(process);
        }
      }
      setHasNewTasks(newTasks.length > 0);
      handledTasks.current = processes;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processes]);

  const handleClick = useCallback<MouseEventHandler>(
    (ev) => {
      if (!isOpen) {
        Analytics.track(EventType.openProgressOverview);
        setIsOpen(true);
        setHasNewTasks(false);
        ev.stopPropagation();
      }
    },
    [isOpen],
  );

  const hasUploadTask = useAppSelector(selectHasUploadTasks);

  return (
    <>
      <CloudActivityMenuButton
        ref={button}
        onClick={handleClick}
        processes={processes}
        showBadge={hasNewTasks}
        disabled={processes.length === 0}
      />
      <Popover
        open={isOpen}
        anchorEl={button.current}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          zIndex: calculateProgressMenuZIndex(ProgressMenuLayer.base),
        }}
      >
        <Stack
          component="div"
          sx={{
            width: "400px",
            maxHeight: "550px",
            borderRadius: "5px",
            px: 2.25,
            py: 0,
            my: 3,
            overflow: "hidden",
          }}
        >
          <FaroText
            variant="heading16"
            fontWeight={FontWeights.SemiBold}
            color={grey[800]}
          >
            Cloud activity
          </FaroText>

          <Box component="div" sx={{ px: 0.75, overflow: "auto" }}>
            <ProgressOverviewFeed processes={processes} />
          </Box>

          <Divider sx={{ borderColor: grey[200] }} />
          {hasUploadTask && (
            <Stack direction="row" spacing={0.5} sx={{ pt: 2 }}>
              <InfoIcon sx={{ color: "yellow600", height: "0.7em", pt: 0.3 }} />
              <Box
                component="div"
                sx={{ fontSize: "0.75em", color: "gray700" }}
              >
                Progress will be lost only if you close the browser while
                uploading.
              </Box>
            </Stack>
          )}
        </Stack>
      </Popover>
    </>
  );
}

const selectHasUploadTasks = createSelector(
  [(state: RootState) => state.backgroundTasks.backgroundTasks],
  (tasks: BackgroundTask[]) =>
    tasks.some(
      (t) =>
        (t.type === "FileUpload" || t.type === "MultiFileUpload") &&
        isBackgroundTaskActive(t.state),
    ),
);
